// pages/EstimationAssurancePage.jsx
import React, { useState, useEffect } from "react";
import i18next from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
const getLocaleCode = () => {
    const language = i18next.resolvedLanguage || i18next.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
};
const formatCurrency = (value) => new Intl.NumberFormat(getLocaleCode(), {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(value) || 0);
export default function EstimationAssurancePage() {
    useTranslation();
    const { artworkId } = useParams();
    const { user } = useAuth();
    const [artwork, setArtwork] = useState(null);
    const [step, setStep] = useState(1);
    const [estimationData, setEstimationData] = useState({
        methodType: "", // "declaree" ou "agreee"
        declaredValue: "",
        expertName: "",
        expertDate: "",
        expertDocument: null,
        purchasePrice: "",
        purchaseDate: "",
        purchaseProof: null,
        comparablesSources: [],
        condition: "excellent",
        provenance: "",
        marketDemand: "",
        needsExpert: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (artworkId && user?.id) {
            loadArtwork();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artworkId, user]);
    const loadArtwork = async () => {
        try {
            const { data, error } = await supabase
                .from("artworks")
                .select("*")
                .eq("id", artworkId)
                .single();
            if (error)
                throw error;
            setArtwork(data);
            // Pré-remplir avec données existantes
            if (data.value) {
                setEstimationData(prev => ({
                    ...prev,
                    declaredValue: data.value,
                    purchasePrice: data.acquisitionDetails?.price || ""
                }));
            }
        }
        catch (error) {
            console.error("Erreur chargement œuvre:", error);
        }
    };
    const calculateRecommendedValue = () => {
        const purchase = parseFloat(estimationData.purchasePrice) || 0;
        if (!purchase)
            return null;
        // Inflation estimée + marge de remplacement
        const inflationRate = 0.02; // 2% par an
        const replacementMargin = 0.15; // 15% marge remplacement
        const yearsSincePurchase = estimationData.purchaseDate
            ? new Date().getFullYear() - new Date(estimationData.purchaseDate).getFullYear()
            : 0;
        const inflatedValue = purchase * Math.pow(1 + inflationRate, yearsSincePurchase);
        const recommendedValue = inflatedValue * (1 + replacementMargin);
        return Math.round(recommendedValue);
    };
    const getJustificationScore = () => {
        let score = 0;
        let maxScore = 0;
        // Critères de justification
        const criteria = [
            { label: i18next.t("estimationAssurance.ui.factureDAchat"), has: !!estimationData.purchaseProof, weight: 3 },
            { label: i18next.t("estimationAssurance.ui.expertiseAgreee"), has: !!estimationData.expertDocument, weight: 4 },
            { label: i18next.t("estimationAssurance.ui.certificatDAuthenticite"), has: Array.isArray(artwork?.documents) && artwork.documents.some(d => d.type === "certificat"), weight: 2 },
            { label: i18next.t("estimationAssurance.ui.historiqueProvenance"), has: Array.isArray(artwork?.provenance) && artwork.provenance.length > 0, weight: 2 },
            { label: i18next.t("estimationAssurance.ui.uvresComparables"), has: estimationData.comparablesSources.length > 0, weight: 2 },
            { label: i18next.t("estimationAssurance.ui.etatExcellent"), has: estimationData.condition === "excellent", weight: 1 }
        ];
        criteria.forEach(c => {
            maxScore += c.weight;
            if (c.has)
                score += c.weight;
        });
        return { score, maxScore, percentage: Math.round((score / maxScore) * 100) };
    };
    const justificationScore = getJustificationScore();
    return (<div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-serif mb-2"><Trans i18nKey="estimationAssurance.ui.estimationPourAssurance"/></h1>
          <p className="text-gray-600"><Trans i18nKey="estimationAssurance.ui.constituezUnDossierSolidePourJustifierVotreValeurDeclaree"/> </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        
        {/* Info pédagogique */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-2"><Trans i18nKey="estimationAssurance.ui.valeurDeclareeVsValeurAgreee"/> </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              <strong><Trans i18nKey="estimationAssurance.ui.valeurDeclaree"/></strong><Trans i18nKey="estimationAssurance.ui.vousEstimezVousMemeLaValeurAvecJustificatifsPlus"/> </p>
            <p>
              <strong><Trans i18nKey="estimationAssurance.ui.valeurAgreee"/></strong><Trans i18nKey="estimationAssurance.ui.expertiseParUnProfessionnelAgreeCnesObligatoireAuDela"/> </p>
          </div>
        </div>

        {/* Étape 1 : Choix de la méthode */}
        {step === 1 && (<div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationAssurance.ui.quelleMethodeDEvaluationChoisir"/> </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Valeur déclarée */}
                <button onClick={() => {
                setEstimationData({ ...estimationData, methodType: "declaree" });
                setStep(2);
            }} className="group text-left border-2 border-gray-200 rounded-lg p-6 hover:border-black transition">
                  <div className="text-4xl mb-4">📝</div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:underline"><Trans i18nKey="estimationAssurance.ui.valeurDeclaree"/> </h3>
                  <p className="text-sm text-gray-600 mb-4"><Trans i18nKey="estimationAssurance.ui.vousEstimezVousMemeAvecDesJustificatifsFactureComparables"/> </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p><Trans i18nKey="estimationAssurance.ui.rapideEtSimple"/></p>
                    <p><Trans i18nKey="estimationAssurance.ui.recommandeJusquA80000"/></p>
                    <p><Trans i18nKey="estimationAssurance.ui.coutGratuitDansVotreAbonnement"/></p>
                  </div>
                </button>

                {/* Valeur agréée */}
                <button onClick={() => {
                setEstimationData({ ...estimationData, methodType: "agreee", needsExpert: true });
                setStep(2);
            }} className="group text-left border-2 border-gray-200 rounded-lg p-6 hover:border-black transition">
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-4xl">🎓</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold"><Trans i18nKey="estimationAssurance.ui.expertRequis"/> </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:underline"><Trans i18nKey="estimationAssurance.ui.valeurAgreee"/> </h3>
                  <p className="text-sm text-gray-600 mb-4"><Trans i18nKey="estimationAssurance.ui.expertiseParUnProfessionnelAgreeCnesChambreNationaleDes"/> </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p><Trans i18nKey="estimationAssurance.ui.obligatoire80000"/></p>
                    <p><Trans i18nKey="estimationAssurance.ui.reconnueParTousLesAssureurs"/></p>
                    <p><Trans i18nKey="estimationAssurance.ui.cout150500SelonUvre"/></p>
                  </div>
                </button>
              </div>
            </div>

            {/* Calcul rapide */}
            {artwork?.value && (<div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold mb-4"><Trans i18nKey="estimationAssurance.ui.votreUvreActuelle"/></h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600"><Trans i18nKey="estimationAssurance.ui.valeurActuelleDansArtkiv"/></p>
                    <p className="text-2xl font-bold">{formatCurrency(artwork.value)}</p>
                  </div>
                  <div className="text-right">
                    {artwork.value > 80000 ? (<span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded"><Trans i18nKey="estimationAssurance.ui.expertiseRecommandee"/> </span>) : (<span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded"><Trans i18nKey="estimationAssurance.ui.valeurDeclareeSuffisante"/> </span>)}
                  </div>
                </div>
              </div>)}
          </div>)}

        {/* Étape 2 : Constitution du dossier */}
        {step === 2 && estimationData.methodType === "declaree" && (<div className="space-y-6">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-600 hover:text-black mb-4">
              <span>←</span><Trans i18nKey="estimationAssurance.ui.changerDeMethode"/> </button>

            {/* Formulaire valeur déclarée */}
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationAssurance.ui.constituerVotreDossierDeJustification"/> </h2>

              <div className="space-y-6">
                {/* Valeur déclarée */}
                <div>
                  <label className="block font-semibold mb-2"><Trans i18nKey="estimationAssurance.ui.valeurDeclareePourLAssurance"/> </label>
                  <input type="number" value={estimationData.declaredValue} onChange={(e) => setEstimationData({
                ...estimationData,
                declaredValue: e.target.value
            })} placeholder={i18next.t("estimationAssurance.ui.ex15000")} className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-black text-lg font-semibold"/>
                  <p className="text-xs text-gray-500 mt-1"><Trans i18nKey="estimationAssurance.ui.valeurDeRemplacementPrixDAchatInflationMarge10"/> </p>
                </div>

                {/* Prix d'achat */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2"><Trans i18nKey="estimationAssurance.ui.prixDAchatInitial"/></label>
                    <input type="number" value={estimationData.purchasePrice} onChange={(e) => setEstimationData({
                ...estimationData,
                purchasePrice: e.target.value
            })} placeholder={i18next.t("estimationAssurance.ui.ex12000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                  </div>
                  <div>
                    <label className="block font-medium mb-2"><Trans i18nKey="estimationAssurance.ui.dateDAchat"/></label>
                    <input type="date" value={estimationData.purchaseDate} onChange={(e) => setEstimationData({
                ...estimationData,
                purchaseDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                  </div>
                </div>

                {/* Calcul auto */}
                {calculateRecommendedValue() && (<div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <p className="text-sm font-medium text-blue-900 mb-1"><Trans i18nKey="estimationAssurance.ui.suggestionBaseeSurInflationMargeRemplacement"/> </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(calculateRecommendedValue())}
                    </p>
                    <button onClick={() => setEstimationData({
                    ...estimationData,
                    declaredValue: calculateRecommendedValue()
                })} className="text-xs text-blue-700 underline hover:text-blue-900 mt-2"><Trans i18nKey="estimationAssurance.ui.utiliserCetteValeur"/> </button>
                  </div>)}

                {/* Documents justificatifs */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4"><Trans i18nKey="estimationAssurance.ui.documentsJustificatifs"/></h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-2">
                        <span className="text-green-600">✓</span><Trans i18nKey="estimationAssurance.ui.factureDAchatRecuFortementRecommande"/> </label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setEstimationData({
                ...estimationData,
                purchaseProof: e.target.files[0]
            })} className="w-full text-sm"/>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block"><Trans i18nKey="estimationAssurance.ui.sourcesComparablesOptionnel"/> </label>
                      <textarea value={estimationData.comparablesSources.join("\n")} onChange={(e) => setEstimationData({
                ...estimationData,
                comparablesSources: e.target.value.split("\n").filter(s => s.trim())
            })} placeholder={i18next.t("estimationAssurance.ui.exArtpriceChristieSSothebyS")} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black text-sm"/>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block"><Trans i18nKey="estimationAssurance.ui.etatDeConservation"/></label>
                      <select value={estimationData.condition} onChange={(e) => setEstimationData({
                ...estimationData,
                condition: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black">
                        <option value="excellent">{i18next.t("estimationAssurance.ui.conditionExcellent")}</option>
                        <option value="tres-bon"><Trans i18nKey="estimationAssurance.ui.tresBon"/></option>
                        <option value="bon">{i18next.t("estimationAssurance.ui.conditionGood")}</option>
                        <option value="moyen">{i18next.t("estimationAssurance.ui.conditionFair")}</option>
                        <option value="restauration"><Trans i18nKey="estimationAssurance.ui.necessiteRestauration"/></option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(3)} className="w-full mt-8 px-6 py-3 bg-black text-white rounded hover:bg-gray-800 font-medium"><Trans i18nKey="estimationAssurance.ui.analyserLeDossier"/> </button>
            </div>
          </div>)}

        {/* Étape 2b : Valeur agréée - Demande expert */}
        {step === 2 && estimationData.methodType === "agreee" && (<div className="space-y-6">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-600 hover:text-black mb-4">
              <span>←</span><Trans i18nKey="estimationAssurance.ui.changerDeMethode"/> </button>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationAssurance.ui.demanderUneExpertiseAgreee"/> </h2>

              <p className="text-gray-700 mb-6"><Trans i18nKey="estimationAssurance.ui.unExpertAgreeCnesPartenaireDArtkivVaEvaluer"/> </p>

              <ul className="space-y-2 text-sm text-gray-700 mb-8">
                <li className="flex gap-2">
                  <span className="text-green-600">✓</span>
                  <span><Trans i18nKey="estimationAssurance.ui.authenticiteCertificatsProvenanceSignature"/></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">✓</span>
                  <span><Trans i18nKey="estimationAssurance.ui.etatDeConservationRestaurationsAlterations"/></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">✓</span>
                  <span><Trans i18nKey="estimationAssurance.ui.rareteEtDemandeDuMarcheActuel"/></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">✓</span>
                  <span><Trans i18nKey="estimationAssurance.ui.historiqueDeLUvreEtDeLArtiste"/></span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600">✓</span>
                  <span><Trans i18nKey="estimationAssurance.ui.comparablesRecentsVentesAuxEncheresGaleries"/></span>
                </li>
              </ul>

              <div className="bg-gray-50 border border-gray-200 rounded p-6 mb-6">
                <h4 className="font-semibold mb-3"><Trans i18nKey="estimationAssurance.ui.tarifsIndicatifs"/></h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span><Trans i18nKey="estimationAssurance.ui.uvre10000"/></span>
                    <span className="font-semibold">150-250 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span><Trans i18nKey="estimationAssurance.ui.uvre10k100k"/></span>
                    <span className="font-semibold">250-400 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span><Trans i18nKey="estimationAssurance.ui.uvre100000"/></span>
                    <span className="font-semibold"><Trans i18nKey="estimationAssurance.ui.surDevis"/></span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-medium mb-2"><Trans i18nKey="estimationAssurance.ui.valeurEstimeeOptionnel"/></label>
                  <input type="number" value={estimationData.declaredValue} onChange={(e) => setEstimationData({
                ...estimationData,
                declaredValue: e.target.value
            })} placeholder={i18next.t("estimationAssurance.ui.ex150000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                  <p className="text-xs text-gray-500 mt-1"><Trans i18nKey="estimationAssurance.ui.pourOrienterLExpertNonContractuel"/> </p>
                </div>

                <div>
                  <label className="block font-medium mb-2"><Trans i18nKey="estimationAssurance.ui.messagePourLExpert"/></label>
                  <textarea placeholder={i18next.t("estimationAssurance.ui.informationsComplementairesUrgencePreferences")} rows="4" className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button onClick={() => alert(i18next.t("estimationAssurance.ui.demandeEnvoyeeAuxExpertsArtkiv"))} className="flex-1 px-6 py-3 bg-black text-white rounded hover:bg-gray-800 font-medium"><Trans i18nKey="estimationAssurance.ui.demanderUnDevisExpert"/> </button>
                <a href="mailto:experts@artkiv.com" className="flex-1 px-6 py-3 border-2 border-black text-black rounded hover:bg-black hover:text-white transition font-medium text-center"><Trans i18nKey="estimationAssurance.ui.contacterDirectement"/> </a>
              </div>
            </div>
          </div>)}

        {/* Étape 3 : Analyse et recommandations */}
        {step === 3 && (<div className="space-y-6">
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-gray-600 hover:text-black mb-4">
              <span>←</span><Trans i18nKey="estimationAssurance.ui.modifierLeDossier"/> </button>

            {/* Score de justification */}
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationAssurance.ui.analyseDeVotreDossier"/> </h2>

              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium"><Trans i18nKey="estimationAssurance.ui.forceDeJustification"/></span>
                  <span className="text-2xl font-bold">{justificationScore.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div className={`h-4 rounded-full transition-all ${justificationScore.percentage >= 70 ? 'bg-green-500' :
                justificationScore.percentage >= 50 ? 'bg-yellow-500' :
                    'bg-red-500'}`} style={{ width: `${justificationScore.percentage}%` }}/>
                </div>
                <p className="text-xs text-gray-500 mt-2"><Trans i18nKey="estimationAssurance.ui.scoreBaseSur"/> {justificationScore.score}/{justificationScore.maxScore}<Trans i18nKey="estimationAssurance.ui.criteres"/> </p>
              </div>

              {/* Résumé */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-sm text-gray-600 mb-1"><Trans i18nKey="estimationAssurance.ui.valeurDeclaree"/></p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(estimationData.declaredValue)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1"><Trans i18nKey="estimationAssurance.ui.typeDAssuranceRecommande"/></p>
                  <p className="text-sm font-semibold">
                    {parseFloat(estimationData.declaredValue) > 4000
                ? i18next.t("estimationAssurance.ui.assuranceSpecialiseeObjetDArt") : i18next.t("estimationAssurance.ui.extensionAssuranceHabitation")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1"><Trans i18nKey="estimationAssurance.ui.primeEstimee"/>{(parseFloat(estimationData.declaredValue) * 0.005).toFixed(0)}<Trans i18nKey="estimationAssurance.ui.an"/> </p>
                </div>
              </div>

              {/* Recommandations */}
              <div className={`border-2 rounded-lg p-6 ${justificationScore.percentage >= 70 ? 'border-green-200 bg-green-50' :
                justificationScore.percentage >= 50 ? 'border-yellow-200 bg-yellow-50' :
                    'border-red-200 bg-red-50'}`}>
                <h3 className="font-semibold mb-4">
                  {justificationScore.percentage >= 70 ? i18next.t("estimationAssurance.ui.dossierSolide") :
                justificationScore.percentage >= 50 ? i18next.t("estimationAssurance.ui.dossierARenforcer") : i18next.t("estimationAssurance.ui.dossierInsuffisant")}
                </h3>

                <ul className="space-y-2 text-sm">
                  {!estimationData.purchaseProof && (<li className="flex gap-2">
                      <span>•</span>
                      <span><strong><Trans i18nKey="estimationAssurance.ui.ajoutezUneFactureDAchat"/></strong><Trans i18nKey="estimationAssurance.ui.cEstLeJustificatifLePlusImportant"/></span>
                    </li>)}
                  {!estimationData.expertDocument && parseFloat(estimationData.declaredValue) > 80000 && (<li className="flex gap-2">
                      <span>•</span>
                      <span><strong><Trans i18nKey="estimationAssurance.ui.expertiseObligatoire"/></strong><Trans i18nKey="estimationAssurance.ui.votreUvreDepasse80000"/></span>
                    </li>)}
                  {estimationData.comparablesSources.length === 0 && (<li className="flex gap-2">
                      <span>•</span>
                      <span><Trans i18nKey="estimationAssurance.ui.ajoutezDes"/> <strong><Trans i18nKey="estimationAssurance.ui.sourcesDeComparables"/></strong><Trans i18nKey="estimationAssurance.ui.artpriceMaisonsDeVentes"/></span>
                    </li>)}
                  {!(Array.isArray(artwork?.provenance) && artwork.provenance.length > 0) && (<li className="flex gap-2">
                      <span>•</span>
                      <span><Trans i18nKey="estimationAssurance.ui.completezL"/><strong><Trans i18nKey="estimationAssurance.ui.historiqueDeProvenance"/></strong><Trans i18nKey="estimationAssurance.ui.dansLaFicheUvre"/></span>
                    </li>)}
                  {estimationData.condition !== "excellent" && estimationData.condition !== "tres-bon" && (<li className="flex gap-2">
                      <span>•</span>
                      <span><Trans i18nKey="estimationAssurance.ui.etatMoyenEnvisagezUne"/> <strong>{i18next.t("estimationAssurance.ui.restoration")}</strong><Trans i18nKey="estimationAssurance.ui.avantAssurance"/></span>
                    </li>)}
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-8 pt-6 border-t space-y-4">
                <h4 className="font-semibold"><Trans i18nKey="estimationAssurance.ui.prochainesEtapes"/></h4>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => alert(i18next.t("estimationAssurance.ui.dossierEnregistreDansLUvre"))} className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800 font-medium"><Trans i18nKey="estimationAssurance.ui.enregistrerDansLUvre"/> </button>
                  <Link to={`/artwork/${artworkId}`} className="px-6 py-3 border-2 border-gray-300 rounded hover:border-black font-medium"><Trans i18nKey="estimationAssurance.ui.retourALaFiche"/> </Link>
                  <a href="mailto:assurance@artkiv.com" className="px-6 py-3 border-2 border-gray-300 rounded hover:border-black font-medium"><Trans i18nKey="estimationAssurance.ui.contacterUnCourtierPartenaire"/> </a>
                </div>
              </div>
            </div>

            {/* Info assureurs */}
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-lg font-semibold mb-4"><Trans i18nKey="estimationAssurance.ui.aSavoirSurLAssurance"/></h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    <strong><Trans i18nKey="estimationAssurance.ui.renouvellementExpertise"/></strong><Trans i18nKey="estimationAssurance.ui.tousLes10AnsArtAncienOu2Ans"/> </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    <strong><Trans i18nKey="estimationAssurance.ui.couvertureClouAClou"/></strong><Trans i18nKey="estimationAssurance.ui.protegeDurantTransportEtExpositionTemporaire"/> </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    <strong>{i18next.t("estimationAssurance.ui.franchise")}</strong><Trans i18nKey="estimationAssurance.ui.verifiezLeMontantSouvent510DeLaValeur"/> </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    <strong><Trans i18nKey="estimationAssurance.ui.securite"/></strong><Trans i18nKey="estimationAssurance.ui.certainsAssureursExigentAlarmeCoffreOuSurveillance"/> </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>
                    <strong><Trans i18nKey="estimationAssurance.ui.declarationModification"/></strong><Trans i18nKey="estimationAssurance.ui.informezVotreAssureurDeTouteRestaurationOuChangementD"/> </span>
                </li>
              </ul>
            </div>
          </div>)}

        {/* Ressources */}
        <div className="mt-12 bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-lg font-serif mb-4"><Trans i18nKey="estimationAssurance.ui.ressourcesUtiles"/></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-sm mb-2"><Trans i18nKey="estimationAssurance.ui.expertsPartenairesArtkiv"/></h4>
              <ul className="text-sm space-y-1">
                <li>
                  <a href="mailto:experts@artkiv.com" className="text-blue-600 hover:underline"><Trans i18nKey="estimationAssurance.ui.demanderUneMiseEnRelation"/> </a>
                </li>
                <li>
                  <a href="https://www.cnes-expertises.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><Trans i18nKey="estimationAssurance.ui.annuaireCnesExpertsAgrees"/> </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-2"><Trans i18nKey="estimationAssurance.ui.courtiersSpecialises"/></h4>
              <ul className="text-sm space-y-1">
                <li>
                  <a href="mailto:assurance@artkiv.com" className="text-blue-600 hover:underline"><Trans i18nKey="estimationAssurance.ui.nosCourtiersPartenaires"/> </a>
                </li>
                <li>
                  <Link to="/fiche/assurance" className="text-blue-600 hover:underline"><Trans i18nKey="estimationAssurance.ui.guideAssuranceUvresDArt"/> </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-600">
              <strong><Trans i18nKey="estimationAssurance.ui.pourLesFamilyOffices"/></strong><Trans i18nKey="estimationAssurance.ui.artkivProposeUnServiceDedieDeGestionPatrimonialeIncluant"/> <a href="mailto:familyoffice@artkiv.com" className="text-blue-600 hover:underline ml-1"><Trans i18nKey="estimationAssurance.ui.contactezNotreEquipe"/> </a>
            </p>
          </div>
        </div>
      </main>
    </div>);
}
